import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import revenueApi from '../../../utils/api/revenue';
import type { UtilityCostFormState } from '../index';

interface AddAccountRevenueShareProperties {
  closeModal: (successMessage?: string) => void;
  revenueType?: 'account' | 'station';
  open?: boolean;
  utilityCost?: UtilityCostFormState;
}

interface RevenueShareFormState {
  accountCustomId: string;
  stationId: string;
  partnerRevenueSharePercentage: number | null;
  cpoRevenueSharePercentage: number | null;
}

interface ConfiguredShare {
  partnerShare: number;
  cpoShare: number;
}

const defaultFormState: RevenueShareFormState = {
  accountCustomId: '',
  stationId: '',
  partnerRevenueSharePercentage: null,
  cpoRevenueSharePercentage: null,
};

/**
 * Add Revenue Share side panel form component, shared by both the account
 * and station revenue management tabs. Lets the user pick a partner account
 * (and, in station mode, one of its stations) and set the partner/CPO
 * revenue share split - the two percentage fields are kept balanced to
 * always sum to 100.
 * @param {AddAccountRevenueShareProperties} properties - The properties
 * @param {(successMessage?: string) => void} properties.closeModal - Closes the side panel, optionally
 *   passing a success message so the parent can show a success modal and refresh
 * @param {'account' | 'station'} [properties.revenueType] - Whether this panel is adding an
 *   account-level or station-level revenue share; station mode shows an extra station dropdown
 * @param {boolean} [properties.open] - Whether the side panel is currently open; the account
 *   list is only fetched while open so the API isn't called while the panel is hidden
 * @param {UtilityCostFormState} [properties.utilityCost] - The default utility cost percentages
 * @returns {any} The rendered component
 */
function AddAccountRevenueShare({
  closeModal,
  revenueType = 'account',
  open = false,
  utilityCost,
}: AddAccountRevenueShareProperties) {
  const { t } = useTranslation('revenue');
  const isStationMode = revenueType === 'station';
  const [accountOptions, setAccountOptions] = useState<{ label: string; value: string }[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [stationOptions, setStationOptions] = useState<{ label: string; value: string }[]>([]);
  const [stationsLoading, setStationsLoading] = useState(false);
  const [formState, setFormState] = useState<RevenueShareFormState>(defaultFormState);
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [configsLoading, setConfigsLoading] = useState(false);

  const [configuredPartnerShare, setConfiguredPartnerShare] = useState<ConfiguredShare | null>(null);
  const [configuredStations, setConfiguredStations] = useState<any[]>([]);

  const configuredPartnerShareReference = useRef<ConfiguredShare | null>(null);
  const configuredStationsReference = useRef<any[]>([]);
  const utilityCostReference = useRef<UtilityCostFormState | undefined>(utilityCost);
  const openReference = useRef<boolean>(open);

  useEffect(() => {
    openReference.current = open;
  }, [open]);

  useEffect(() => {
    configuredPartnerShareReference.current = configuredPartnerShare;
  }, [configuredPartnerShare]);

  useEffect(() => {
    configuredStationsReference.current = configuredStations;
  }, [configuredStations]);

  useEffect(() => {
    utilityCostReference.current = utilityCost;
  }, [utilityCost]);

  /**
   * Fetches the partner account list for the account select field
   * @returns {Promise<void>} Resolves once the account list is loaded
   */
  const fetchAccountOptions = async () => {
    setAccountsLoading(true);
    try {
      const response = await revenueApi.fetchAccountList();
      setAccountOptions(
        (response?.data?.results || response?.data || []).map((account: any) => ({
          label: account?.accountName,
          value: account?.accountCustomId,
        })),
      );
    } catch (error) {
      setAccountOptions([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  /**
   * Fetches the charging stations belonging to the selected account for the
   * station select field
   * @param {string} accountCustomId - The selected account's custom id
   * @returns {Promise<void>} Resolves once the station list is loaded
   */
  const fetchStationOptions = async (accountCustomId: string) => {
    setStationsLoading(true);
    try {
      const response = await revenueApi.fetchChargingStationsList(accountCustomId);
      setStationOptions(
        (response?.data?.results || response?.data || []).map((station: any) => ({
          label: station?.name,
          value: station?.id || station?._id,
        })),
      );
    } catch (error) {
      setStationOptions([]);
    } finally {
      setStationsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAccountOptions();
      const initialPartnerPercentage = utilityCost?.partnerRevenueSharePercentage ?? null;
      const initialCpoPercentage = utilityCost?.cpoRevenueSharePercentage ?? null;
      setFormState({
        accountCustomId: '',
        stationId: '',
        partnerRevenueSharePercentage: initialPartnerPercentage,
        cpoRevenueSharePercentage: initialCpoPercentage,
      });
      setConfiguredPartnerShare(null);
      setConfiguredStations([]);
      configuredPartnerShareReference.current = null;
      configuredStationsReference.current = [];
      setShowFormErrors(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && !formState.accountCustomId) {
      setFormState((previous) => {
        if (previous.accountCustomId) return previous;
        return {
          ...previous,
          partnerRevenueSharePercentage: utilityCost?.partnerRevenueSharePercentage ?? null,
          cpoRevenueSharePercentage: utilityCost?.cpoRevenueSharePercentage ?? null,
        };
      });
    }
  }, [utilityCost, open]);

  /**
   * Function to update the selected account. In station mode, switching the
   * account clears the previously selected station and reloads the station
   * options for the newly selected account. Also checks whether the selected
   * partner has an existing configured revenue share and pre-populates the
   * share inputs accordingly.
   * @param {string} name - The field name
   * @param {string | number} value - The selected account custom id
   * @returns {Promise<void>} Resolves once account configuration is loaded and applied
   */
  const handleAccountChange = async (name: string, value: string | number) => {
    const accountCustomId = String(value);

    if (!accountCustomId) {
      setConfiguredPartnerShare(null);
      setConfiguredStations([]);
      configuredPartnerShareReference.current = null;
      configuredStationsReference.current = [];
      setStationOptions([]);
      setFormState((previous) => ({
        ...previous,
        accountCustomId: '',
        stationId: '',
        partnerRevenueSharePercentage: utilityCost?.partnerRevenueSharePercentage ?? null,
        cpoRevenueSharePercentage: utilityCost?.cpoRevenueSharePercentage ?? null,
      }));
      return;
    }

    setFormState((previous) => ({
      ...previous,
      accountCustomId,
      stationId: '',
    }));

    if (isStationMode) {
      setStationOptions([]);
      fetchStationOptions(accountCustomId);
    }

    setConfigsLoading(true);
    try {
      const partnerPromise = revenueApi.fetchRevenueConfigurationList({
        type: 'Partner',
        accountCustomId: [accountCustomId],
      });

      const stationPromise = isStationMode
        ? revenueApi.fetchRevenueConfigurationList({
            type: 'Station',
            accountCustomId: [accountCustomId],
            limit: 1000,
          })
        : Promise.resolve(null);

      const [partnerResponse, stationResponse] = await Promise.all([
        partnerPromise,
        stationPromise,
      ]);

      if (!openReference.current) return;

      const partnerResults = partnerResponse?.data?.results || [];
      const matchedPartner = partnerResults.find(
        (account: any) => account.accountCustomId === accountCustomId,
      );

      const partnerShareValue =
        matchedPartner?.revenuePercentage ?? matchedPartner?.partnerRevenuePercentage;

      let partnerShareObject: ConfiguredShare | null = null;
      if (
        partnerShareValue !== null &&
        partnerShareValue !== undefined &&
        partnerShareValue !== ''
      ) {
        const partnerNumber = Number(partnerShareValue);
        const cpoNumber =
          matchedPartner?.cpoRevenuePercentage !== null &&
          matchedPartner?.cpoRevenuePercentage !== undefined &&
          matchedPartner?.cpoRevenuePercentage !== ''
            ? Number(matchedPartner.cpoRevenuePercentage)
            : Math.round((100 - partnerNumber) * 100) / 100;
        partnerShareObject = { partnerShare: partnerNumber, cpoShare: cpoNumber };
      }

      setConfiguredPartnerShare(partnerShareObject);
      configuredPartnerShareReference.current = partnerShareObject;

      if (isStationMode && stationResponse) {
        const stationResults = stationResponse?.data?.results || [];
        setConfiguredStations(stationResults);
        configuredStationsReference.current = stationResults;
      }

      const initialPartnerPercentage =
        partnerShareObject !== null
          ? partnerShareObject.partnerShare
          : (utilityCost?.partnerRevenueSharePercentage ?? null);
      const initialCpoPercentage =
        partnerShareObject !== null
          ? partnerShareObject.cpoShare
          : (utilityCost?.cpoRevenueSharePercentage ?? null);

      setFormState((previous) => {
        if (previous.accountCustomId !== accountCustomId) return previous;
        return {
          ...previous,
          stationId: '',
          partnerRevenueSharePercentage: initialPartnerPercentage,
          cpoRevenueSharePercentage: initialCpoPercentage,
        };
      });
    } catch (error) {
      if (!openReference.current) return;
      setFormState((previous) => {
        if (previous.accountCustomId !== accountCustomId) return previous;
        return {
          ...previous,
          stationId: '',
          partnerRevenueSharePercentage: utilityCost?.partnerRevenueSharePercentage ?? null,
          cpoRevenueSharePercentage: utilityCost?.cpoRevenueSharePercentage ?? null,
        };
      });
    } finally {
      if (openReference.current) {
        setConfigsLoading(false);
      }
    }
  };

  /**
   * Function to update the selected station. Checks if the chosen station
   * has an existing configured revenue share and pre-populates the share
   * inputs, falling back to the partner's share or default utility cost.
   * @param {string} name - The field name
   * @param {string | number} value - The selected station id
   */
  const handleStationChange = (name: string, value: string | number) => {
    const stationId = String(value);

    const partnerShareObject = configuredPartnerShareReference.current;
    const currentUtilityCost = utilityCostReference.current;

    const fallbackPartnerShare =
      partnerShareObject !== null
        ? partnerShareObject.partnerShare
        : (currentUtilityCost?.partnerRevenueSharePercentage ?? null);
    const fallbackCpoShare =
      partnerShareObject !== null
        ? partnerShareObject.cpoShare
        : (currentUtilityCost?.cpoRevenueSharePercentage ?? null);

    if (!stationId) {
      setFormState((previous) => ({
        ...previous,
        stationId: '',
        partnerRevenueSharePercentage: fallbackPartnerShare,
        cpoRevenueSharePercentage: fallbackCpoShare,
      }));
      return;
    }

    const matchedStation = configuredStationsReference.current.find(
      (station: any) =>
        String(station.stationId) === stationId ||
        String(station.chargingStationId) === stationId ||
        String(station.id) === stationId ||
        String(station._id) === stationId,
    );

    const stationShareValue =
      matchedStation?.revenuePercentage ?? matchedStation?.partnerRevenuePercentage;

    if (
      stationShareValue !== null &&
      stationShareValue !== undefined &&
      stationShareValue !== ''
    ) {
      const partnerNumber = Number(stationShareValue);
      const cpoNumber =
        matchedStation?.cpoRevenuePercentage !== null &&
        matchedStation?.cpoRevenuePercentage !== undefined &&
        matchedStation?.cpoRevenuePercentage !== ''
          ? Number(matchedStation.cpoRevenuePercentage)
          : Math.round((100 - partnerNumber) * 100) / 100;

      setFormState((previous) => ({
        ...previous,
        stationId,
        partnerRevenueSharePercentage: partnerNumber,
        cpoRevenueSharePercentage: cpoNumber,
      }));
    } else {
      setFormState((previous) => ({
        ...previous,
        stationId,
        partnerRevenueSharePercentage: fallbackPartnerShare,
        cpoRevenueSharePercentage: fallbackCpoShare,
      }));
    }
  };

  /**
   * Function to update a revenue share percentage field. Partner and CPO
   * shares are kept balanced to always sum to 100 - editing one field
   * auto-fills the other with the remainder.
   * @param {string} name - The field name (partnerRevenueSharePercentage or cpoRevenueSharePercentage)
   * @param {string | number} value - The new field value
   */
  const handlePercentageChange = (name: string, value: string | number) => {
    const numericValue = value === '' ? null : Number(value);
    const counterpartName =
      name === 'partnerRevenueSharePercentage'
        ? 'cpoRevenueSharePercentage'
        : 'partnerRevenueSharePercentage';
    const counterpartValue =
      numericValue === null ? null : Math.round((100 - numericValue) * 100) / 100;

    setFormState((previous) => ({
      ...previous,
      [name]: numericValue,
      [counterpartName]: counterpartValue,
    }));
  };

  const isFormComplete =
    !!formState.accountCustomId &&
    (!isStationMode || !!formState.stationId) &&
    formState.partnerRevenueSharePercentage !== null &&
    formState.cpoRevenueSharePercentage !== null;

  /**
   * Function to submit the new revenue share. Used for both account-level and
   * station-level submissions - the payload only carries a stationId in station mode.
   * @returns {Promise<void>} Resolves once the save request completes
   */
  const handleSubmit = async () => {
    setShowFormErrors(true);
    if (!isFormComplete) return;

    setSubmitting(true);
    try {
      await revenueApi.addAccountRevenueShare({
        accountCustomId: formState.accountCustomId,
        ...(isStationMode ? { stationId: formState.stationId } : {}),
        partnerRevenueSharePercentage: Number(formState.partnerRevenueSharePercentage),
        cpoRevenueSharePercentage: Number(formState.cpoRevenueSharePercentage),
      });
      closeModal('Revenue share added successfully');
    } catch (error) {
      setSubmitting(false);
    }
  };

  return (
    <div className="add-account-revenue-share">
      <p className="add-account-revenue-share__title">
        {t('addRevenueShare', 'Add Revenue Share')}
      </p>

      <div className="add-account-revenue-share__form">
        <SelectFiled
          name="accountCustomId"
          inputLabel={t('accountName', 'Account Name')}
          options={accountOptions}
          loading={accountsLoading}
          selectedValue={formState.accountCustomId}
          onChangeValue={handleAccountChange}
          error={showFormErrors && !formState.accountCustomId}
          errorMessage={t('accountNameRequired', 'Account Name Required')}
          valueRequired
          enableSearch
        />

        {isStationMode && (
          <SelectFiled
            name="stationId"
            inputLabel={t('stationName', 'Station Name')}
            placeholder={t('selectStationName', 'Select Station Name')}
            options={stationOptions}
            loading={stationsLoading || configsLoading}
            selectedValue={formState.stationId}
            onChangeValue={handleStationChange}
            error={showFormErrors && !formState.stationId}
            errorMessage={t('stationNameRequired', 'Station Name Required')}
            valueRequired
            enableSearch
            disabled={!formState.accountCustomId || configsLoading}
          />
        )}

        <CustomInput
          inputLabel={t('partnerRevenueShare', 'Partner Revenue Share %')}
          name="partnerRevenueSharePercentage"
          type="number"
          value={formState.partnerRevenueSharePercentage}
          onChangeValue={handlePercentageChange}
          placeholder=""
          maxValue={100}
          decimalLimit={2}
          valueRequired
          disabled={configsLoading}
          error={showFormErrors && formState.partnerRevenueSharePercentage === null}
          errorMessage={t('partnerRevenueShareRequired', 'Partner Revenue Share Required')}
        />

        <CustomInput
          inputLabel={t('cpoRevenueShare', 'CPO Revenue Share %')}
          name="cpoRevenueSharePercentage"
          type="number"
          value={formState.cpoRevenueSharePercentage}
          onChangeValue={handlePercentageChange}
          placeholder=""
          maxValue={100}
          decimalLimit={2}
          valueRequired
          disabled={configsLoading}
          error={showFormErrors && formState.cpoRevenueSharePercentage === null}
          errorMessage={t('cpoRevenueShareRequired', 'CPO Revenue Share Required')}
        />

        <div className="add-account-revenue-share__footer">
          <MyButton
            label={t('cancel')}
            buttonType="error"
            onClickFunc={() => closeModal()}
            disableButton={submitting}
          />
          <MyButton
            label={t('submit')}
            buttonType="submit"
            onClickFunc={handleSubmit}
            disableButton={submitting}
            loading={submitting}
          />
        </div>
      </div>
    </div>
  );
}

AddAccountRevenueShare.defaultProps = {
  revenueType: 'account',
  open: false,
  utilityCost: undefined,
};

export default AddAccountRevenueShare;
